import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@heroui/react";
import { useChatContext } from "../../providers/ChatContext";
import { ChatHeader } from "./ChatHeader";
import { ChatMessage } from "./ChatMessage";
import { ChatSuggestions } from "./ChatSuggestions";
import { ChatInput } from "./ChatInput";
import { ChatCompletion } from "./ChatCompletion";

interface ChatPanelProps {
  isOpen: boolean;
}

export function ChatPanel({ isOpen }: ChatPanelProps) {
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    sessionId,
    isLoading,
    isComplete,
    suggestions,
    tripRequest,
    language,
    setLanguage,
    sendMessage,
    startNewChat,
    closeChat,
    clearSession,
    startSession,
  } = useChatContext();

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, [messages, isComplete, isOpen]);

  const handleLanguageChange = async (newLang: "en" | "es") => {
    setLanguage(newLang);
    clearSession();
    await startSession(newLang);
  };

  const handleViewTrip = () => {
    if (tripRequest?.requestId) {
      navigate(`/my-trip/${tripRequest.requestId}`);
    }
  };

  return (
    <div
      className={`fixed z-[60] bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out
        right-0 bottom-0 w-full h-[85dvh] rounded-t-2xl
        sm:right-6 sm:bottom-24 sm:w-[380px] sm:h-[560px] sm:rounded-2xl
        ${isOpen ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-4 opacity-0 pointer-events-none"}
      `}
    >
      <ChatHeader
        onNewChat={startNewChat}
        onClose={closeChat}
        isLoading={isLoading}
        language={language}
        onLanguageChange={handleLanguageChange}
      />

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {!sessionId && isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Spinner size="lg" color="primary" />
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage key={message.messageId} message={message} />
            ))}

            {isLoading && messages.length > 0 && (
              <div className="flex justify-start mb-3">
                <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <span
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    />
                    <span
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    />
                  </div>
                </div>
              </div>
            )}

            {isComplete && (
              <ChatCompletion
                tripRequest={tripRequest}
                onNewChat={startNewChat}
                onViewTrip={handleViewTrip}
              />
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {suggestions.length > 0 && (
        <ChatSuggestions
          suggestions={suggestions}
          onSelect={sendMessage}
          disabled={isLoading}
        />
      )}

      <ChatInput onSend={sendMessage} disabled={isLoading || isComplete} />
    </div>
  );
}
